import React, {
  type ReactNode,
  useRef,
  useCallback,
  useState,
  useEffect,
} from 'react';
import {
  NumberField as NumberFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';
import { getNumberOfDecimals } from '../shared/numeric-input-utils';
import { useInvokeCallback } from '../shared/use-invoke-callback';

import { type NumberInputFieldProps } from './number-input-field-props';

export type NumberInputProps = {
  children: ReactNode;
};

export const NumberInput = ({ children }: NumberInputProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    readOnly,
    onBlur,
    onFocus,
    FieldLayout,
    hasError,
    validation,
  } = useFieldPropsV2<NumberInputFieldProps>();
  const multipleOf = validation?.number?.multipleOf || 1;
  const maximum = validation?.number?.maximum;
  const minimum = validation?.number?.minimum;
  const numberValue = valueToNumber(value);
  const [internalValue, setInternalValue] = useState<number>(numberValue);
  const invokeBlur = useInvokeCallback(onBlur);

  const labelElement = findChildOfType(children, NumberInput.Label);
  const inputElement = findChildOfType(children, NumberInput.Input);
  const descriptionElement = findChildOfType(children, NumberInput.Description);
  const errorElement = findChildOfType(children, NumberInput.Error);

  const handleChange = useCallback(
    (newValue: number) => {
      if (newValue === undefined || Number.isNaN(newValue)) {
        onChange('');
        setInternalValue(NaN);
        return;
      }

      const numberOfDecimals = getNumberOfDecimals(multipleOf);
      const validValue = Number(newValue.toFixed(numberOfDecimals));

      onChange(validValue);
      setInternalValue(validValue);
    },
    [multipleOf, onChange],
  );

  useEffect(() => {
    const newValue = valueToNumber(value);

    if (!newValue && !internalValue) {
      return;
    }

    if (newValue !== internalValue) {
      setInternalValue(newValue);
    }
  }, [internalValue, value]);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <NumberFieldRAC
          value={internalValue}
          onChange={handleChange}
          isDisabled={disabled}
          isRequired={required}
          isReadOnly={readOnly}
          isInvalid={hasError}
          onBlur={invokeBlur}
          onFocus={onFocus}
          minValue={minimum ?? undefined}
          maxValue={maximum ?? undefined}
        >
          {inputElement}
        </NumberFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { t } = useTranslation();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { id, placeholder, validation, label, required } =
    useFieldPropsV2<NumberInputFieldProps>();
  const multipleOf = validation?.number?.multipleOf || 1;
  const ref = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={ref}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      aria-label={label}
      aria-roledescription={t('field.number.aria-role-description')}
      className={className}
      inputMode={multipleOf ? 'decimal' : 'numeric'}
      placeholder={placeholder}
      required={required}
    />
  );
};

NumberInput.Label = Label;
NumberInput.Input = Input;
NumberInput.Description = Description;
NumberInput.Error = Error;

const valueToNumber = (value: NumberInputFieldProps['value']): number => {
  if (value === null || value === undefined || value === '') {
    return NaN;
  }

  if (typeof value === 'number') {
    return value;
  }

  const parsed = Number(value);

  return Number.isNaN(parsed) ? NaN : parsed;
};
